<template>
  <v-dialog
    :model-value="modelValue"
    class="bq-bulk-dialog-root"
    content-class="bq-bulk-dialog-root"
    max-width="640"
    persistent
    scrollable
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <SnackbarComp :SnackbarComponent="snackbar" />

    <v-card rounded="xl" class="user-dialog-card bq-lead-dialog bq-bulk-dialog">
      <div class="dialog-header bq-lead-dialog__header">
        <div class="bq-lead-dialog__header-main">
          <div class="dialog-header__icon bq-lead-dialog__icon-wrap" aria-hidden="true">
            <v-icon icon="mdi-file-upload-outline" size="22" />
          </div>
          <div class="bq-lead-dialog__head-copy">
            <span class="dialog-header__title">Bulk lead upload</span>
            <span class="bq-lead-dialog__subtitle">
              Import leads from a CSV or Excel file into one or more products.
            </span>
          </div>
        </div>
        <v-btn
          icon
          variant="flat"
          size="small"
          class="dialog-close-btn bq-lead-dialog__close"
          rounded="lg"
          :disabled="uploading"
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="dialog-body bq-lead-dialog__body">
        <div class="bq-bulk-dialog__upload">
          <label class="bq-lead-dialog__field">
            <span class="bq-lead-dialog__label">Products</span>
            <v-select
              v-model="selectedContexts"
              :items="contextOptions"
              item-title="title"
              item-value="value"
              placeholder="Select one or more products"
              prepend-inner-icon="mdi-bookshelf"
              variant="solo"
              density="comfortable"
              hide-details="auto"
              multiple
              chips
              closable-chips
              flat
              :loading="contextsLoading"
              no-data-text="No products available"
              class="bq-lead-dialog__input"
            />
            <span class="bq-lead-dialog__field-hint">
              One lead is created per file row per selected product. Rows matching an
              existing lead (by the product's unique identifier) update it instead.
            </span>
            <button
              v-if="selectedContexts.length"
              type="button"
              class="bq-bulk-dialog__template-link"
              @click="downloadTemplate"
            >
              <v-icon icon="mdi-download-outline" size="16" />
              Download Excel template for {{ selectedContexts.length === 1 ? selectedContexts[0] : 'these products' }}
            </button>
          </label>

          <label class="bq-lead-dialog__field">
            <span class="bq-lead-dialog__label">Force-add to campaign (optional)</span>
            <v-select
              v-model="forceTaskId"
              :items="campaignOptions"
              item-title="title"
              item-value="value"
              placeholder="Don't force-add -- only sync automatically"
              prepend-inner-icon="mdi-bullseye-arrow"
              variant="solo"
              density="comfortable"
              hide-details="auto"
              clearable
              flat
              :loading="campaignsLoading"
              no-data-text="No active campaigns"
              class="bq-lead-dialog__input"
            />
            <span class="bq-lead-dialog__field-hint">
              Every lead this upload creates or updates also gets added directly to this
              campaign, even if the campaign's own sync is turned off or its filters wouldn't
              normally match these leads.
            </span>
          </label>

          <label class="bq-lead-dialog__field">
            <span class="bq-lead-dialog__label">Lead file</span>
            <v-file-input
              v-model="csvFile"
              accept=".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              placeholder="Choose a .csv, .xls or .xlsx file"
              prepend-icon=""
              prepend-inner-icon="mdi-file-delimited-outline"
              variant="solo"
              density="comfortable"
              hide-details="auto"
              flat
              clearable
              :loading="parsing"
              class="bq-lead-dialog__input"
            />
            <span class="bq-lead-dialog__field-hint">
              Any column headers work -- match each one below to a field, or leave it
              unmatched to keep it as a custom field (today's default behavior).
            </span>
          </label>

          <p v-if="parseError" class="bq-bulk-dialog__parse-error">
            {{ parseError }}
          </p>

          <div v-if="fileHeaders.length" class="bq-bulk-dialog__mapping">
            <span class="bq-lead-dialog__label">
              Column mapping
              <span v-if="aiSuggesting" class="bq-bulk-dialog__mapping-ai">
                <v-progress-circular indeterminate size="12" width="2" />
                matching the rest with AI...
              </span>
            </span>
            <div class="bq-bulk-dialog__mapping-rows">
              <div
                v-for="(header, index) in fileHeaders"
                :key="`${header}-${index}`"
                class="bq-bulk-dialog__mapping-row"
              >
                <span class="bq-bulk-dialog__mapping-source" :title="header">{{ header }}</span>
                <v-icon icon="mdi-arrow-right" size="16" class="bq-bulk-dialog__mapping-arrow" />
                <v-select
                  :model-value="columnMapping[index]"
                  :items="mappingOptionsFor(index)"
                  item-title="title"
                  item-value="value"
                  variant="solo"
                  density="comfortable"
                  hide-details="auto"
                  flat
                  class="bq-bulk-dialog__mapping-select"
                  @update:model-value="(value) => onMappingChange(index, value)"
                />
              </div>
            </div>

            <p v-if="duplicateTargetLabels.length" class="bq-bulk-dialog__mapping-error">
              More than one column is mapped to: {{ duplicateTargetLabels.join(', ') }}. Pick a
              different field for each.
            </p>
            <p v-else-if="!requiredFieldMapped" class="bq-bulk-dialog__mapping-error">
              Map a column to Contact Number -- every lead needs one.
            </p>
            <p v-else class="bq-bulk-dialog__mapping-hint">
              Matching against existing leads by: {{ identifierFieldLabels }}.
            </p>
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="dialog-footer bq-lead-dialog__footer">
        <v-spacer />
        <v-btn
          variant="flat"
          class="bq-lead-dialog__cancel"
          :disabled="uploading"
          @click="close"
        >
          Cancel
        </v-btn>
        <v-btn
          variant="flat"
          class="bq-lead-dialog__submit"
          :loading="uploading"
          :disabled="!canUpload"
          @click="startUpload"
        >
          Upload File
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import SnackbarComp from '@/components/Extras/SnackbarComp.vue'
import { fetchContexts } from '@/api/contextsApi'
import { getBulkLeadUploadUrl, suggestLeadColumnMapping } from '@/api/leadsApi'
import { fetchCampaigns } from '@/api/campaignsApi'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'
import { unwrapApiList, unwrapApiPayload } from '@/utils/graphql'
import { normalizeContextsList } from '@/utils/contexts'
import { normalizeCampaignsList } from '@/utils/campaigns'
import {
  parseSpreadsheetToRows,
  buildMappedCsv,
  mergeTargetFields,
  suggestFieldForHeader,
  downloadLeadTemplate,
  REQUIRED_TARGET_FIELD,
} from '@/utils/bulkLeadColumnMapping'
import '@/styles/business-queries/lead-form-dialog.css'
import '@/styles/business-queries/bulk-upload-dialog.css'

defineOptions({ name: 'BulkLeadUploadDialog' })

const props = defineProps({
  modelValue: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue', 'uploaded'])

const store = useStore()
const snackbar = ref({})
const uploading = ref(false)

const contexts = ref([])
const contextsLoading = ref(false)
const selectedContexts = ref([])
const csvFile = ref(null)

const campaigns = ref([])
const campaignsLoading = ref(false)
const forceTaskId = ref(null)

const parsing = ref(false)
const parseError = ref('')
const fileRows = ref([]) // [headerRow, ...dataRows] as parsed from the file
const fileHeaders = ref([])
// { [headerIndex]: targetFieldKey | null } -- null means "keep as custom field".
const columnMapping = ref({})
// Indices the user has explicitly changed -- auto re-suggestion (on product
// change) never overwrites these, only untouched columns.
const touchedColumns = ref(new Set())
const aiSuggesting = ref(false)

const contextOptions = computed(() =>
  contexts.value
    .filter((context) => !context.disabled)
    .map((context) => ({
      title: context.context_name,
      value: context.context_name,
    })),
)

// EXCEL-mode campaigns only accept leads via their own agent-mapped upload
// (enforced backend-side too) -- excluded here so the dropdown never offers
// an option the server would reject anyway.
const campaignOptions = computed(() =>
  campaigns.value
    .filter((c) => c.status === 'ACTIVE' && c.distributionMode !== 'EXCEL')
    .map((c) => ({
      title: c.autoAdoptMatchingLeads === false ? `${c.name} (sync off)` : c.name,
      value: c.id,
    })),
)

/** Enabled fields across the selected products -- the mapping dropdowns' targets. */
const targetFields = computed(() => mergeTargetFields(contexts.value, selectedContexts.value))

// Same field list the mapping dropdowns above already use, so the template
// can never drift out of sync with what a real upload actually expects.
function downloadTemplate() {
  const label = selectedContexts.value.length === 1 ? selectedContexts.value[0] : ''
  void downloadLeadTemplate(targetFields.value, { productLabel: label })
}

function mappingOptionsFor(index) {
  const currentTarget = columnMapping.value[index]
  return [
    { title: 'Keep as custom field', value: null },
    ...targetFields.value.map((field) => ({
      title: field.label,
      // A field already mapped elsewhere is still shown (so the current
      // selection stays visible) but disabled to prevent a second column
      // silently overwriting the first.
      value: field.name,
      disabled: field.name !== currentTarget && isTargetMappedElsewhere(field.name, index),
    })),
  ]
}

function isTargetMappedElsewhere(fieldName, excludeIndex) {
  return Object.entries(columnMapping.value).some(
    ([idx, target]) => Number(idx) !== excludeIndex && target === fieldName,
  )
}

const duplicateTargetLabels = computed(() => {
  const counts = new Map()
  for (const target of Object.values(columnMapping.value)) {
    if (!target) continue
    counts.set(target, (counts.get(target) || 0) + 1)
  }
  return targetFields.value
    .filter((field) => (counts.get(field.name) || 0) > 1)
    .map((field) => field.label)
})

const requiredFieldMapped = computed(() =>
  Object.values(columnMapping.value).includes(REQUIRED_TARGET_FIELD),
)

const identifierFieldLabels = computed(() => {
  const labels = new Set()
  for (const name of selectedContexts.value) {
    const context = contexts.value.find((item) => String(item?.context_name ?? '').trim() === name)
    const fieldKey = context?.unique_identifier_field || REQUIRED_TARGET_FIELD
    const field = targetFields.value.find((f) => f.name === fieldKey)
    labels.add(field?.label || fieldKey)
  }
  return [...labels].join(', ')
})

function autoSuggestMapping() {
  const next = { ...columnMapping.value }
  fileHeaders.value.forEach((header, index) => {
    if (touchedColumns.value.has(index)) return
    next[index] = suggestFieldForHeader(header, targetFields.value)
  })
  columnMapping.value = next
}

// Fallback for whatever the deterministic alias/exact-match pass above
// couldn't resolve -- one batched call per file (not per column), and only
// fired when there's actually something left unmatched, so a well-named
// file never triggers it at all. Only offered target fields that aren't
// already claimed by another column, so the model can't introduce a fresh
// duplicate; a failed/garbage response just leaves those columns unmatched,
// same as if this didn't exist.
async function runAiColumnMappingFallback() {
  const unmatchedIndices = fileHeaders.value
    .map((_, index) => index)
    .filter((index) => !touchedColumns.value.has(index) && columnMapping.value[index] == null)
  if (!unmatchedIndices.length) return

  const claimed = new Set(Object.values(columnMapping.value).filter(Boolean))
  const availableTargets = targetFields.value.filter((field) => !claimed.has(field.name))
  if (!availableTargets.length) return

  aiSuggesting.value = true
  try {
    const response = await suggestLeadColumnMapping({
      headers: unmatchedIndices.map((index) => fileHeaders.value[index]),
      target_fields: availableTargets.map((field) => ({ name: field.name, label: field.label })),
    })
    if (!isApiSuccess(response)) return
    const payload = unwrapApiPayload(response) ?? response
    const suggestions = payload?.mapping || {}

    const next = { ...columnMapping.value }
    for (const [subIndexKey, fieldName] of Object.entries(suggestions)) {
      const subIndex = Number(subIndexKey)
      if (!Number.isInteger(subIndex) || subIndex < 0 || subIndex >= unmatchedIndices.length) continue
      const originalIndex = unmatchedIndices[subIndex]
      // The user may have edited this column manually while the call was
      // in flight -- never override that.
      if (touchedColumns.value.has(originalIndex) || next[originalIndex] != null) continue
      if (!fieldName || claimed.has(fieldName)) continue
      next[originalIndex] = fieldName
      claimed.add(fieldName)
    }
    columnMapping.value = next
  } catch {
    // Non-fatal.
  } finally {
    aiSuggesting.value = false
  }
}

async function parseSelectedFile(file) {
  parseError.value = ''
  fileRows.value = []
  fileHeaders.value = []
  columnMapping.value = {}
  touchedColumns.value = new Set()
  if (!file) return

  parsing.value = true
  try {
    const rows = await parseSpreadsheetToRows(file)
    fileRows.value = rows
    fileHeaders.value = rows[0].map((h) => String(h ?? '').trim() || '(blank column)')
    autoSuggestMapping()
  } catch (error) {
    parseError.value = getApiMessage(error, 'Could not read this file.')
    return
  } finally {
    parsing.value = false
  }
  void runAiColumnMappingFallback()
}

watch(csvFile, (value) => {
  const file = Array.isArray(value) ? value[0] : value
  void parseSelectedFile(file)
})

watch(selectedContexts, () => {
  if (fileHeaders.value.length) autoSuggestMapping()
})

// Only a real user edit marks a column "touched" -- autoSuggestMapping's own
// assignment must NOT count, or the very first auto-suggest pass would mark
// every column touched and permanently block re-suggestion on product change.
function onMappingChange(index, value) {
  columnMapping.value = { ...columnMapping.value, [index]: value }
  touchedColumns.value.add(index)
}

const mappingValid = computed(() => {
  if (!fileHeaders.value.length) return true
  return requiredFieldMapped.value && duplicateTargetLabels.value.length === 0
})

const canUpload = computed(() => {
  const file = Array.isArray(csvFile.value) ? csvFile.value[0] : csvFile.value
  return Boolean(
    selectedContexts.value.length &&
      file &&
      !uploading.value &&
      !parsing.value &&
      !parseError.value &&
      mappingValid.value,
  )
})

function showError(message) {
  snackbar.value = {
    SnackbarVmodel: true,
    SnackbarColor: 'error',
    SnackbarText: message,
  }
}

async function loadContexts() {
  const orgId = String(store.getters.sessionContext?.org_id || '').trim()
  if (!orgId) {
    contexts.value = []
    return
  }

  contextsLoading.value = true
  try {
    const response = await fetchContexts({ org_id: orgId })
    contexts.value = isApiSuccess(response)
      ? normalizeContextsList(unwrapApiList(response))
      : []
  } catch {
    contexts.value = []
  } finally {
    contextsLoading.value = false
  }
}

async function loadCampaigns() {
  campaignsLoading.value = true
  try {
    const response = await fetchCampaigns()
    campaigns.value = isApiSuccess(response) ? normalizeCampaignsList(unwrapApiList(response)) : []
  } catch {
    campaigns.value = []
  } finally {
    campaignsLoading.value = false
  }
}

async function startUpload() {
  const file = Array.isArray(csvFile.value) ? csvFile.value[0] : csvFile.value
  if (!file || !selectedContexts.value.length || !fileRows.value.length) return

  uploading.value = true
  try {
    // The file was already parsed for the mapping step -- rebuild it as CSV
    // with headers renamed per that mapping (untouched columns keep their
    // original header, i.e. remain a custom field, same as before this
    // feature existed) instead of re-reading/re-converting the raw file.
    let converted
    try {
      const csv = await buildMappedCsv(fileRows.value, columnMapping.value)
      converted = {
        body: new Blob([csv], { type: 'text/csv' }),
        filename: `${String(file.name || 'leads').replace(/\.(xlsx|xls|csv)$/i, '')}.csv`,
      }
    } catch (error) {
      showError(getApiMessage(error, 'Could not prepare the file for upload.'))
      return
    }

    const urlResponse = await getBulkLeadUploadUrl({
      context_names: selectedContexts.value,
      filename: converted.filename,
      force_task_id: forceTaskId.value || undefined,
    })
    if (!isApiSuccess(urlResponse)) {
      showError(getApiMessage(urlResponse, 'Failed to get upload URL'))
      return
    }

    const payload = unwrapApiPayload(urlResponse) ?? urlResponse
    const uploadUrl = String(payload?.upload_url ?? '').trim()
    if (!uploadUrl) {
      showError('Upload URL missing from response.')
      return
    }

    // Raw PUT with Content-Type: text/csv and no other headers (per contract).
    const putResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': 'text/csv' },
      body: converted.body,
    })
    if (!putResponse.ok) {
      showError(`Upload failed (${putResponse.status}). Please try again.`)
      return
    }

    csvFile.value = null
    // upload_id lets callers correlate against listBulkLeadUploads rows.
    emit('uploaded', { upload_id: String(payload?.upload_id ?? '').trim() })
    close()
  } catch (error) {
    showError(getApiMessage(error, 'Upload failed'))
  } finally {
    uploading.value = false
  }
}

function close() {
  emit('update:modelValue', false)
}

watch(
  () => props.modelValue,
  (open) => {
    if (!open) return
    selectedContexts.value = []
    forceTaskId.value = null
    csvFile.value = null
    void loadContexts()
    void loadCampaigns()
  },
)
</script>
